'use client'

import Link from 'next/link'
import { useContext, useState } from 'react'

import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { useGetProductTagsList } from '../../../../../entities/createProduct/queries'
import { useGetProductsListQuery } from '../../../../../entities/productsList/queries'
import {
  GetCategoryInfoResponse,
  GetProductsListResponse,
  ProductListItem,
  SortOrder,
} from '../../../../../shared/api/generated'
import { colors } from '../../../../../shared/constants/colors'
import { useModal } from '../../../../../shared/hooks/useModal'
import FilterStorage from '../../../../../shared/storage/FilterStorage/FilterStorage'
import { FilterButton } from '../../../../../shared/ui-kit/actual/FilterButton'
import { PaginationComponent } from '../../../../../shared/ui-kit/actual/PaginationComponent'
import { ProductCardsList } from '../../../../../shared/ui-kit/actual/ProductCardsList'
import { ProductListHeader } from '../../../../../shared/ui-kit/actual/ProductListHeader'
import type { ProductListCardVariant } from '../../../../../shared/ui-kit/actual/ProductListCard'
import {
  MobileTabletFilterAction,
  ProductListingLayout,
} from '../../../../../shared/ui-kit/actual/ProductListingLayout'
import { SortButton } from '../../../../../shared/ui-kit/actual/SortButton'
import { AbsoluteLoader } from '../../../../../shared/ui-kit/actual/components/AbsoluteLoader'
import { MainHeader } from '../../../../../shared/ui-kit/actual/headers/MainHeader'
import { RightModal } from '../../../../../shared/ui-kit/actual/modals/RightModal'
import FilterWindowNew from '../../../../../shared/ui-kit/actual/modalsContent/FilterWindowNew'
import { MainMenuWindow } from '../../../../../shared/ui-kit/actual/modalsContent/MainMenuWindow'
import SortModalWindow from '../../../../../shared/ui-kit/actual/modalsContent/SortModalWindow'
import {
  breakpoints,
  mobileTabletView,
  mobileView,
  tabletDesktopView,
} from '../../../../../shared/ui-kit/breakPoints'
import { MainSearchComponent } from '../../../../../shared/ui-kit/deprecated/MainSearchComponent'
import {
  FilterSubWrapper,
  ImgButton,
  MobileView,
} from '../../../../../shared/ui-kit/projectComponents'
import { SsrClientProvider } from '../../../../providers/SsrClientProvider/SsrClientProvider'

interface CategorySubPageViewProps {
  categoryId: string
  categoryInfo?: GetCategoryInfoResponse
  parentCategoryId?: string
  parentCategoryName?: string
  productsData?: GetProductsListResponse
  sectionId: string
  sectionName?: string
}

const BasePage = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 12px 60px;
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMax});
  margin: 0 auto;
  @media (width >= ${breakpoints.tabletMin}) {
    padding: 0 36px 60px;
    overflow: auto;
  }
`

const BaseWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  padding: 0 24px;
  margin-inline: auto;

  @media screen and (width <= ${breakpoints.tabletMin}) {
    padding: 0;
  }
`

const CommonHeaderWrapper = styled.div`
  width: 100%;
  ${tabletDesktopView}
`

const FlexColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`

const FlexRowBetween = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
`

const TopBlock = styled(FlexRowBetween)`
  align-items: center;
  margin: 24px 0 12px;
  ${mobileView}
`

const BackLink = styled(Link)`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0px;
  color: #000000;
  text-align: center;
  margin: 0;
`

const Breadcrumbs = styled.nav`
  font-size: 18px;
  color: #000;
  margin: 32px 0 24px;
  width: 100%;

  a {
    color: #000;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  ${tabletDesktopView}
`

const ProductsWrapper = styled.div`
  min-height: 580px;
  width: 100%;
`

const ProductActions = styled(FilterSubWrapper)`
  @media (width < ${breakpoints.tabletMin}) {
    width: auto;
    justify-content: flex-end;
    gap: 10px;
  }
`

const BottomMenu = styled.nav`
  position: fixed;
  bottom: -1px;
  left: 0px;
  margin: 0;
  border-top: 1px solid ${colors.lightGray};
  background-color: white;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 30px 30px 0px 0px;
  z-index: 2000;
  box-shadow: 0px -5px 60px 0px rgba(0, 0, 0, 0.05);
  ${mobileTabletView}
`

const BottomMenuInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 19px 37px 20px 37px;
`

const BottomItem = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 40px;
  width: 40px;
  row-gap: 4px;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  img {
    width: 20px;
    height: 20px;
  }

  p {
    color: ${colors.grey};
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
  }
`

const ITEMS_PER_PAGE = 300
const ALL_TAGS_ID = 999999

const CategorySubPageContent = observer(function CategorySubPageContent({
  categoryId,
  categoryInfo,
  parentCategoryId,
  parentCategoryName,
  productsData,
  sectionId,
  sectionName,
}: CategorySubPageViewProps) {
  const { sorting } = useContext(FilterStorage)
  const { setViewModal, viewModal } = useModal()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false)
  const [activeTagId, setActiveTagId] = useState(ALL_TAGS_ID)
  const [pageId, setPageId] = useState(0)
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [productListVariant, setProductListVariant] = useState<ProductListCardVariant>('card')

  const categoryName = categoryInfo?.name ?? 'Категория'
  const sectionTitle = sectionName?.trim() ? sectionName : 'Раздел'
  const parentTitle = parentCategoryName?.trim() ? parentCategoryName : 'Категория'
  const parentHref = parentCategoryId
    ? `/sections/${sectionId}/${parentCategoryId}`
    : `/sections/${sectionId}`
  const backHref = parentHref

  const { data: tagsData } = useGetProductTagsList({
    SectionId: categoryInfo?.sectionId,
    Skip: 0,
    Take: 100,
  })
  const sectionTags =
    tagsData?.items
      ?.filter(tag => typeof tag.id === 'number' && tag.name)
      .map(tag => ({ id: tag.id!, name: tag.name })) ?? []
  const tags = sectionTags.length ? [{ id: ALL_TAGS_ID, name: 'Все' }, ...sectionTags] : []
  const isInitialRequest =
    pageId === 0 &&
    sortOrder === 'asc' &&
    activeTagId === ALL_TAGS_ID &&
    !sorting.dayPriceFrom &&
    !sorting.dayPriceTo &&
    !sorting.orderType &&
    !sorting.premiumOnly &&
    !sorting.ratingFrom &&
    !sorting.numberFromToFilters?.length &&
    !sorting.numberOneOfFilters?.length &&
    !sorting.stringExactFilters?.length &&
    !sorting.stringOneOfFilters?.length &&
    !sorting.stringTermFilters?.length

  const { data, isLoading } = useGetProductsListQuery(
    {
      categoryId: Number(categoryId),
      dayPriceFrom: sorting.dayPriceFrom || null,
      dayPriceTo: sorting.dayPriceTo || null,
      numberFromToFilters: sorting.numberFromToFilters,
      numberOneOfFilters: sorting.numberOneOfFilters,
      orderType: sorting.orderType,
      premiumOnly: sorting.premiumOnly,
      productTagsIds: activeTagId === ALL_TAGS_ID ? [] : [activeTagId],
      ratingFrom: sorting.ratingFrom || undefined,
      skip: pageId * ITEMS_PER_PAGE,
      sortOrder,
      stringExactFilters: sorting.stringExactFilters,
      stringOneOfFilters: sorting.stringOneOfFilters,
      stringTermFilters: sorting.stringTermFilters,
      take: ITEMS_PER_PAGE,
    },
    {
      initialData: isInitialRequest ? productsData : undefined,
      keepPreviousData: true,
    }
  )

  const displayedData = isInitialRequest && productsData ? productsData : data
  const items: ProductListItem[] = displayedData?.items ?? []
  const totalPages = Math.max(1, Math.ceil((displayedData?.totalCount ?? 0) / ITEMS_PER_PAGE))

  return (
    <BasePage>
      <AbsoluteLoader isLoading={isLoading} />
      <CommonHeaderWrapper>
        <MainHeader />
      </CommonHeaderWrapper>
      <BaseWrapper>
        <FlexColumn style={{ width: '100%' }}>
          <TopBlock>
            <BackLink href={backHref}>
              <img
                alt={'arrow-back'}
                src={'/images/buttons/arrow-right-black.svg'}
                style={{ transform: 'rotate(180deg)' }}
              />
            </BackLink>
            <FlexRow style={{ flexGrow: 1 }}>
              <PageTitle>{categoryName}</PageTitle>
            </FlexRow>
            <ImgButton
              alt={''}
              onClick={() => setIsMenuOpen(true)}
              src={'/images/buttons/menu-button.svg'}
            />
          </TopBlock>

          <MobileView>
            <MainSearchComponent
              hideFilterButton
              onClickFilter={() => setViewModal(true)}
              setIsFixed={() => null}
            />
          </MobileView>

          <Breadcrumbs>
            <Link href={'/'}>Главная</Link>
            {' / '}
            <Link href={'/sections'}>Разделы</Link>
            {' / '}
            <Link href={`/sections/${sectionId}`}>{sectionTitle}</Link>
            {parentCategoryId ? (
              <>
                {' / '}
                <Link href={parentHref}>{parentTitle}</Link>
              </>
            ) : null}
            {' / '}
            <span>{categoryName}</span>
          </Breadcrumbs>

          <ProductListHeader
            actions={
              <ProductActions>
                <SortButton
                  onClickSort={() => setIsSortMenuOpen(open => !open)}
                  selectedSort={sorting.orderType || 'byDefault'}
                  sortOrder={sortOrder}
                  toggleSort={() => {
                    setSortOrder(order => (order === 'asc' ? 'desc' : 'asc'))
                    setPageId(0)
                  }}
                />
                <MobileTabletFilterAction>
                  <FilterButton onClickFilter={() => setViewModal(true)} />
                </MobileTabletFilterAction>
              </ProductActions>
            }
            activeTagId={activeTagId}
            onTagChange={tagId => {
              setActiveTagId(tagId)
              setPageId(0)
            }}
            onVariantChange={setProductListVariant}
            tags={tags}
            title={categoryName}
            variant={productListVariant}
          />

          <ProductListingLayout
            filters={
              <FilterWindowNew
                categoryId={Number(categoryId)}
                handleFilterModalOpen={() => undefined}
              />
            }
          >
            <ProductsWrapper>
              <ProductCardsList products={items} variant={productListVariant} />
            </ProductsWrapper>

            {displayedData && (
              <PaginationComponent maxPages={totalPages} pageId={pageId} setPageId={setPageId} />
            )}
          </ProductListingLayout>
        </FlexColumn>

        <BottomMenu aria-label={'Нижнее меню'}>
          <BottomMenuInner>
            <BottomItem href={'/orders'}>
              <img alt={''} src={'/images/bottom-menu/cart-gray.svg'} />
              <p>Заказы</p>
            </BottomItem>
            <BottomItem href={'/messages'}>
              <img alt={''} src={'/images/bottom-menu/chat-gray.svg'} />
              <p>Чат</p>
            </BottomItem>
            <BottomItem href={'/blog'}>
              <img alt={''} src={'/images/bottom-menu/blog-gray.svg'} />
              <p>Блог</p>
            </BottomItem>
            <BottomItem href={'/'}>
              <img alt={''} src={'/images/bottom-menu/main-gray.svg'} />
              <p>Главная</p>
            </BottomItem>
            <BottomItem href={'/profile'}>
              <img alt={''} src={'/images/bottom-menu/profile-gray.svg'} />
              <p>Профиль</p>
            </BottomItem>
          </BottomMenuInner>
        </BottomMenu>
      </BaseWrapper>

      <RightModal mobileTabletOnly setViewModal={setViewModal} viewModal={viewModal}>
        <FilterWindowNew
          categoryId={Number(categoryId)}
          handleFilterModalOpen={() => setViewModal(false)}
        />
      </RightModal>
      <RightModal setViewModal={setIsMenuOpen} viewModal={isMenuOpen}>
        <MainMenuWindow handleModalClose={() => setIsMenuOpen(false)} />
      </RightModal>
      <SortModalWindow
        handleMenu={() => setIsSortMenuOpen(open => !open)}
        openMenu={isSortMenuOpen}
        selectedSort={sorting.orderType}
      />
    </BasePage>
  )
})

export function CategorySubPageView(props: CategorySubPageViewProps) {
  const initialUrl = props.parentCategoryId
    ? `/sections/${props.sectionId}/${props.parentCategoryId}/sub/${props.categoryId}`
    : `/sections/${props.sectionId}/sub/${props.categoryId}`

  return (
    <SsrClientProvider initialUrl={initialUrl}>
      <CategorySubPageContent {...props} />
    </SsrClientProvider>
  )
}
